import type { AuthProvider } from '@words-happen/ui-components';
import {
  ChevronLeftIcon,
  SignInPrompt,
  SOCIAL_AUTH_PROVIDERS,
  SocialAuthRow,
  WordsHappenButton,
  WordsHappenDivider,
  WordsHappenFormAlert,
  WordsHappenPasswordField,
  WordsHappenTextField,
} from '@words-happen/ui-components';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useValidatedForm } from '../auth-form';
import { ScreenFrame } from '../screen-frame';
import styles from './register-screen.module.scss';
import {
  normaliseDisplayName,
  PASSWORD_RULES,
  REGISTER_MESSAGES,
  REGISTER_VALIDATORS,
  type RegistrationDetails,
  type RegistrationErrors,
} from './register-validation';

export interface RegisterScreenProps {
  /** Called with the normalised display name, the trimmed email and the password as typed, once all three are valid. */
  onSubmit: (details: RegistrationDetails) => void;
  /** An account is being created: the submit button shows its spinner and is disabled. */
  submitting?: boolean;
  /** A form-level error, e.g. from the server. Shown in an alert above the submit button. */
  error?: React.ReactNode;
  /**
   * Field errors from the server, e.g. a password the policy rejected. Each shows on its field unless the field has
   * an error of its own, until the caller clears it; the first one gets focus when it appears.
   */
  fieldErrors?: RegistrationErrors;
  /**
   * Prefills the email when the screen mounts, e.g. the address a failed sign-in was tried with; the display name then
   * gets focus.
   */
  defaultEmail?: string;
  /** The social providers offered above the form. Email is left out: this is the email route. */
  authProviders?: readonly AuthProvider[];
  /** The providers that can be used; the rest are shown disabled, "coming soon". Every provider when left out. */
  enabledProviders?: readonly AuthProvider[];
  /** A provider whose sign-in is starting: its button shows a spinner and the others are disabled. */
  busyProvider?: AuthProvider;
  onSelectProvider: (provider: AuthProvider) => void;
  onSignIn: () => void;
  onBack: () => void;
  className?: string;
}

declare module 'react' {
  interface InputHTMLAttributes<T> {
    /** Safari's password rules for generated passwords, e.g. "minlength: 15; maxlength: 128;". */
    passwordrules?: string;
  }
}

const EMPTY: RegistrationDetails = { displayName: '', email: '', password: '' };

/**
 * Email and password registration, with the social providers as a shortcut. Presentational only:
 * creating the account, errors from the server and every navigation are the caller's.
 */
export const RegisterScreen: React.FC<RegisterScreenProps> = ({
  onSubmit,
  submitting = false,
  error,
  fieldErrors,
  defaultEmail = '',
  authProviders = SOCIAL_AUTH_PROVIDERS,
  enabledProviders,
  busyProvider,
  onSelectProvider,
  onSignIn,
  onBack,
  className,
}) => {
  const { values, errors, change, blur, validateAll } = useValidatedForm(
    { ...EMPTY, email: defaultEmail },
    REGISTER_VALIDATORS,
  );
  const refs = {
    displayName: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: on mount only, like the prefilled value itself
  useEffect(() => {
    if (defaultEmail) refs.displayName.current?.focus();
  }, []);

  const firstServerError = (Object.keys(refs) as (keyof RegistrationDetails)[]).find((field) => fieldErrors?.[field]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refs are stable; focus only when the server error changes
  useEffect(() => {
    if (firstServerError) refs[firstServerError].current?.focus();
  }, [firstServerError, fieldErrors]);

  const shownError = (field: keyof RegistrationDetails) => errors[field] ?? fieldErrors?.[field];

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const invalid = validateAll();
    if (invalid) {
      refs[invalid].current?.focus();
      return;
    }

    onSubmit({
      displayName: normaliseDisplayName(values.displayName),
      email: values.email.trim(),
      password: values.password,
    });
  };

  return (
    <ScreenFrame className={className}>
      <header className={styles.topBar}>
        <WordsHappenButton
          size="sm"
          className={styles.back}
          aria-label="Back"
          icon={<ChevronLeftIcon size={22} />}
          onClick={onBack}
        />
      </header>

      <main className={styles.main}>
        <h1 className={styles.heading}>Create your free account</h1>
        <p className={styles.subhead}>Keep your scores, streaks and friends on every device.</p>

        <SocialAuthRow
          compact
          className={styles.providers}
          providers={authProviders}
          enabledProviders={enabledProviders}
          busyProvider={busyProvider}
          onSelect={onSelectProvider}
        />
        <WordsHappenDivider label="or" className={styles.divider} />

        <form className={styles.form} aria-label="Create an account with email" noValidate onSubmit={handleSubmit}>
          <WordsHappenTextField
            ref={refs.displayName}
            label="Display name"
            name="displayName"
            autoComplete="nickname"
            autoCapitalize="words"
            spellCheck={false}
            value={values.displayName}
            error={shownError('displayName')}
            onChange={change('displayName')}
            onBlur={blur('displayName')}
          />
          <WordsHappenTextField
            ref={refs.email}
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={values.email}
            error={shownError('email')}
            onChange={change('email')}
            onBlur={blur('email')}
          />
          <WordsHappenPasswordField
            ref={refs.password}
            label="Password"
            name="password"
            autoComplete="new-password"
            passwordrules={PASSWORD_RULES}
            hint={REGISTER_MESSAGES.passwordHint}
            value={values.password}
            error={shownError('password')}
            onChange={change('password')}
            onBlur={blur('password')}
          />

          {error ? <WordsHappenFormAlert>{error}</WordsHappenFormAlert> : null}

          <WordsHappenButton type="submit" variant="coral" size="lg" loading={submitting}>
            Create account
          </WordsHappenButton>
        </form>

        <SignInPrompt className={styles.signIn} onAction={onSignIn} />
      </main>
    </ScreenFrame>
  );
};
